import React from 'react'
import { useQuery } from '@apollo/client'
import { UniversalHeaderLogos } from '../universal-header-logos'
import { useUser } from '../../../hooks/use-user'
import { useUniversalHeaderCart } from '../../../hooks/use-cart'
import { useCountryContext } from '../../../context'
import { UniversalHeader } from '..'
import { InitialPathType, HeaderVariants } from '../types'
import {
  CentreStaticData,
  NationalStaticData,
  GiftCardsStaticData,
} from './fetch-universal-header-static-data'

export type UniversalHeaderOptions =
  | { variant: 'national'; data: NationalStaticData }
  | { variant: 'centre'; data: CentreStaticData }
  | { variant: 'gift-cards'; data: GiftCardsStaticData }
  | { variant: 'direct'; data: NationalStaticData }
  | { variant: 'westfield-plus'; data: NationalStaticData }

function selectLogo(
  variant: HeaderVariants
): React.ComponentProps<typeof UniversalHeaderLogos>['logo'] {
  switch (variant) {
    case 'national':
    case 'centre':
    case 'gift-cards':
      return 'westfield'
    case 'direct':
      return 'direct'
    case 'westfield-plus':
      return 'westfield-plus'
    default:
      return undefined
  }
}

export type Header = React.FC

export function useUniversalHeader(
  options: UniversalHeaderOptions | null
): [Header, (p: InitialPathType) => void] {
  const isGiftCard = options?.variant === 'gift-cards'

  const country = useCountryContext()
  const { user } = useUser(useQuery)
  const cart = useUniversalHeaderCart(useQuery, Boolean(isGiftCard))
  const [navPath, setNavPath] = React.useState<InitialPathType>()

  if (!options || !options.data) {
    return [() => null, setNavPath]
  }

  const { variant, data } = options

  const { centres, giftCards, navigationMenu } = data
  const centre = 'centre' in data ? data.centre : undefined

  return [
    () => (
      <UniversalHeader
        user={user}
        navPath={navPath}
        logo={selectLogo(variant)}
        cart={cart}
        hamburgerMenuProps={{
          centres,
          country,
          giftCards,
          menuId: 'national',
          onClose: () => setNavPath(undefined),
          ...(navigationMenu
            ? {
                shop: {
                  navigationMenu,
                },
              }
            : {}),
          centre,
        }}
        {...(centre ? { logoUrl: `/${centre.slug}` } : {})}
        tabsProps={{
          variant,
          centres,
          centre,
          primaryLinks: data.primaryLinks,
          tertiaryLinks: data.tertiaryLinks,
          openHamburgerMenuAtPath: () => undefined,
        }}
      />
    ),
    setNavPath,
  ]
}
